import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    CreatePromotionPathRuleDTO,
    CreatePromotionRecordDTO,
    ListPromotionHistoryQuery,
    ListPromotionPathRulesQuery,
    ListPromotionRecordsQuery,
    UpdatePromotionPathRuleDTO,
    UpdatePromotionRecordDTO,
} from '@/modules/hr/promotion/domain/types';
import { promotionApi } from './client';

export const promotionKeys = {
    rules: (query: ListPromotionPathRulesQuery) =>
        ['hr', 'promotion', 'rules', query] as const,
    validToPositions: (fromPositionId: string) =>
        ['hr', 'promotion', 'validToPositions', fromPositionId] as const,
    ruleDetail: (id: string) =>
        ['hr', 'promotion', 'rules', 'detail', id] as const,
    records: (query: ListPromotionRecordsQuery) =>
        ['hr', 'promotion', 'records', query] as const,
    recordDetail: (id: string) =>
        ['hr', 'promotion', 'records', 'detail', id] as const,
    history: (query: ListPromotionHistoryQuery) =>
        ['hr', 'promotion', 'history', query] as const,
    employees: ['hr', 'promotion', 'lookup', 'employees'] as const,
    positions: ['hr', 'promotion', 'lookup', 'positions'] as const,
    levels: ['hr', 'promotion', 'lookup', 'levels'] as const,
    employeeCurrentPosition: (employeeId: string) =>
        ['hr', 'promotion', 'employeeCurrentPosition', employeeId] as const,
};

export function usePromotionRulesQuery(query: ListPromotionPathRulesQuery) {
    return useQuery({
        queryKey: promotionKeys.rules(query),
        queryFn: () => promotionApi.listRules(query),
    });
}

/** Rules where fromPositionId = given id, isActive=true. Use to derive valid "to" positions. */
export function useValidToPositionsQuery(fromPositionId: string | undefined) {
    return useQuery({
        queryKey: promotionKeys.validToPositions(fromPositionId ?? ''),
        queryFn: async () => {
            const result = await promotionApi.listRules({
                fromPositionId: fromPositionId!,
                isActive: true,
                limit: 500,
                offset: 0,
            });
            const seen = new Set<string>();
            const options: { value: string; label: string }[] = [];
            for (const r of result.data ?? []) {
                if (r.toPositionId && !seen.has(r.toPositionId)) {
                    seen.add(r.toPositionId);
                    options.push({
                        value: r.toPositionId,
                        label: r.toPositionTitle ?? r.toPositionId,
                    });
                }
            }
            return options;
        },
        enabled: !!fromPositionId?.trim(),
    });
}

export function usePromotionRuleDetailQuery(id: string | undefined) {
    return useQuery({
        queryKey: promotionKeys.ruleDetail(id ?? ''),
        queryFn: () => promotionApi.getRuleById(id!),
        enabled: !!id,
    });
}

export function useCreatePromotionRuleMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreatePromotionPathRuleDTO) =>
            promotionApi.createRule(dto),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ['hr', 'promotion'] }),
    });
}

export function useDeletePromotionRuleMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => promotionApi.deleteRule(id),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ['hr', 'promotion'] }),
    });
}

export function useUpdatePromotionRuleMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            dto,
        }: {
            id: string;
            dto: UpdatePromotionPathRuleDTO;
        }) => promotionApi.updateRule(id, dto),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ['hr', 'promotion'] }),
    });
}

export function usePromotionRecordsQuery(query: ListPromotionRecordsQuery) {
    return useQuery({
        queryKey: promotionKeys.records(query),
        queryFn: () => promotionApi.listRecords(query),
    });
}

export function usePromotionRecordDetailQuery(id: string | undefined) {
    return useQuery({
        queryKey: promotionKeys.recordDetail(id ?? ''),
        queryFn: () => promotionApi.getRecordById(id!),
        enabled: !!id,
    });
}

export function useCreatePromotionRecordMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreatePromotionRecordDTO) =>
            promotionApi.createRecord(dto),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ['hr', 'promotion'] }),
    });
}

export function useApprovePromotionRecordMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => promotionApi.approveRecord(id),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ['hr', 'promotion'] }),
    });
}

export function useUpdatePromotionRecordMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            dto,
        }: {
            id: string;
            dto: UpdatePromotionRecordDTO;
        }) => promotionApi.updateRecord(id, dto),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ['hr', 'promotion'] }),
    });
}

export function usePromotionHistoryQuery(query: ListPromotionHistoryQuery) {
    return useQuery({
        queryKey: promotionKeys.history(query),
        queryFn: () => promotionApi.listHistory(query),
    });
}

export function usePromotionEmployeesLookupQuery() {
    return useQuery({
        queryKey: promotionKeys.employees,
        queryFn: () => promotionApi.listEmployees(),
    });
}

export function usePromotionPositionsLookupQuery() {
    return useQuery({
        queryKey: promotionKeys.positions,
        queryFn: () => promotionApi.listPositions(),
    });
}

export function usePromotionLevelsLookupQuery() {
    return useQuery({
        queryKey: promotionKeys.levels,
        queryFn: () => promotionApi.listLevels(),
    });
}

export function useEmployeeCurrentPositionQuery(employeeId: string | undefined) {
    return useQuery({
        queryKey: promotionKeys.employeeCurrentPosition(employeeId ?? ''),
        queryFn: () => promotionApi.getEmployeeCurrentPosition(employeeId!),
        enabled: !!employeeId?.trim(),
    });
}
